import { useSeoTags } from "@/lib/seo";

/**
 * VOIHU Support — approved verbatim text.
 * Informational page only. No backend endpoints are created or invoked.
 * Layout / spacing / typography mirror /privacy, /terms and /cookies.
 */
export default function SupportPage() {
  useSeoTags({
    title: "Support — VOIHU",
    description:
      "Get help with VOIHU accounts, MESSAGEs, VOICES, payments, privacy, " +
      "moderation and other service-related matters.",
    canonical: "https://voihuworld.com/support",
  });

  return (
    <main className="bg-[color:var(--oh-bg)] text-[color:var(--oh-ink)]"
          data-testid="support-page">
      <article className="max-w-[720px] mx-auto px-6 sm:px-10 pt-16 pb-24">
        <div className="oh-label" data-testid="support-eyebrow">Support</div>

        <h1 className="font-serif-editorial text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mt-4"
            data-testid="support-h1">
          VOIHU Support
        </h1>

        <div className="mt-10 space-y-6 text-base sm:text-[17px] leading-[1.75]"
             data-testid="support-body">
          <p>Need help with VOIHU? We’re here to help.</p>
          <p>
            This page explains how to contact VOIHU about your account, a
            MESSAGE or VOICE, payments, privacy, content, moderation and other
            service-related matters.
          </p>

          <Section title="General support">
            <p>For questions or assistance with VOIHU, contact:</p>
            <p>
              <Mail testId="support-general-email" />
            </p>
            <p>You can use this address for issues involving:</p>
            <List>
              <li>your VOIHU account;</li>
              <li>creating or accessing a MESSAGE;</li>
              <li>adding a VOICE;</li>
              <li>PASS IT ON;</li>
              <li>Global Impact or My Impact;</li>
              <li>technical problems;</li>
              <li>payments or refunds;</li>
              <li>other questions about using VOIHU.</li>
            </List>
            <p>
              Please provide enough information for us to identify the issue,
              but do not send payment-card numbers, passwords or other
              unnecessary sensitive information by email.
            </p>
          </Section>

          <Section title="Payments and refunds">
            <p>
              For questions about a VOIHU payment, duplicate or erroneous
              transactions, or a refund request, contact:
            </p>
            <p>
              <Mail testId="support-payments-email" />
            </p>
            <p>
              Where possible, include the email address associated with your
              VOIHU account and sufficient information to identify the
              relevant transaction.
            </p>
            <p>Do not send your full payment-card number or security code.</p>
            <p>Payments on VOIHU are processed by Stripe.</p>
            <p>Any mandatory consumer rights remain unaffected.</p>
          </Section>

          <Section title="Privacy and data-protection requests">
            <p>
              For questions about your personal data or to exercise applicable
              data-protection rights, contact:
            </p>
            <p>
              <Mail testId="support-privacy-email" />
            </p>
            <p>
              Depending on applicable law, these rights may include access,
              rectification, erasure, restriction, objection and data
              portability.
            </p>
            <p>
              VOIHU may need to verify your identity before acting on a
              request in order to protect your information.
            </p>
            <p>
              For further information, please read our{" "}
              <a href="/privacy" className="underline underline-offset-4"
                 data-testid="support-privacy-link">Privacy Policy</a>.
            </p>
          </Section>

          <Section title="Report content">
            <p>If you believe a MESSAGE or other content on VOIHU:</p>
            <List>
              <li>violates applicable law;</li>
              <li>infringes your rights;</li>
              <li>contains unlawful personal information;</li>
              <li>infringes intellectual-property rights;</li>
              <li>violates VOIHU’s Terms of Service;</li>
              <li>presents another serious safety or abuse concern;</li>
            </List>
            <p>
              please use the reporting mechanism available on VOIHU where
              applicable.
            </p>
            <p>
              If you cannot use the reporting mechanism or need additional
              assistance, contact:
            </p>
            <p>
              <Mail testId="support-report-email" />
            </p>
            <p>
              Please identify the relevant MESSAGE and explain the reason for
              your report.
            </p>
            <p>
              Submitting a report does not guarantee removal. VOIHU will
              assess reports according to applicable law, the Terms of Service
              and the circumstances of the case.
            </p>
          </Section>

          <Section title="Moderation and appeals">
            <p>
              Some VOIHU MESSAGE decisions may involve automated systems.
            </p>
            <p>
              Where VOIHU provides an appeal or human-review mechanism, please
              use that mechanism first.
            </p>
            <p>
              If you are unable to access the relevant mechanism or require
              assistance with an existing review, contact:
            </p>
            <p>
              <Mail testId="support-appeals-email" />
            </p>
            <p>
              Please provide sufficient information to identify the MESSAGE or
              review concerned.
            </p>
          </Section>

          <Section title="Security">
            <p>
              If you believe you have identified a security issue affecting
              VOIHU, contact:
            </p>
            <p>
              <Mail testId="support-security-email" />
            </p>
            <p>
              Please provide enough information for us to understand and
              investigate the issue.
            </p>
            <p>
              Do not use a suspected vulnerability to access another person’s
              account, obtain information you are not authorised to access,
              disrupt the service or damage VOIHU or its users.
            </p>
          </Section>

          <Section title="Legal notices">
            <p>
              Formal legal communications concerning VOIHU may be sent to the
              operator:
            </p>
            <Address>
              <div>CA HOLDING S.R.L.S.</div>
              <div>Viale Italia 8/C</div>
              <div>25126 Brescia (BS)</div>
              <div>Italy</div>
              <div>Italian Tax Code / VAT No.: 04672180983</div>
              <div>REA: BS-632332</div>
              <div>
                Certified Email (PEC):{" "}
                <a href="mailto:caholding@pec.it"
                   className="underline underline-offset-4"
                   data-testid="support-pec-email">caholding@pec.it</a>
              </div>
              <div>Service / trading name: VOIHU</div>
              <div>Website: voihuworld.com</div>
            </Address>
            <p>
              For ordinary support, privacy or service requests, please use:
            </p>
            <p>
              <Mail testId="support-ordinary-email" />
            </p>
          </Section>

          <Section title="Legal documents">
            <p>You can find further information about VOIHU in:</p>
            <ul className="list-disc pl-6 space-y-2 marker:text-[color:var(--oh-ink-3)]">
              <li>
                <a href="/terms" className="underline underline-offset-4"
                   data-testid="support-terms-link">Terms of Service</a>
              </li>
              <li>
                <a href="/privacy" className="underline underline-offset-4"
                   data-testid="support-privacy-doc-link">Privacy Policy</a>
              </li>
              <li>
                <a href="/cookies" className="underline underline-offset-4"
                   data-testid="support-cookies-link">Cookie Policy</a>
              </li>
            </ul>
          </Section>
        </div>
      </article>
    </main>
  );
}


/* ── layout helpers (same style as /privacy, /terms, /cookies) ──────────── */

function Section({ title, children }) {
  return (
    <section className="mt-14 pt-10 border-t oh-hairline">
      <h2 className="font-serif-editorial text-2xl sm:text-3xl leading-[1.15]">
        {title}
      </h2>
      <div className="mt-6 space-y-5">{children}</div>
    </section>
  );
}

function List({ children }) {
  return (
    <ul className="list-disc pl-6 space-y-2 marker:text-[color:var(--oh-ink-3)]">
      {children}
    </ul>
  );
}

function Address({ children }) {
  return (
    <address className="not-italic leading-[1.75] my-2">{children}</address>
  );
}

function Mail({ testId }) {
  return (
    <a href="mailto:support@voihuworld.com"
       className="underline underline-offset-4"
       data-testid={testId}>
      support@voihuworld.com
    </a>
  );
}
